import {
    Box,
    Checkbox,
    List,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { AlertTriangle } from 'lucide-react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import {
    CopySkipReason,
    CopyTargetPlan,
} from '~/domain/services/structureSetService/copyTargetPlan';
import { StructureSetRoiReadModel } from './types';
import { dicomColorToRgbString } from '~/utils/color';

type StructureListPanelProps = {
    filteredRois: StructureSetRoiReadModel[];
    selectedRois: Set<number>;
    disabledRoiNumbers: Set<number>;
    copyPlans: ReadonlyMap<number, CopyTargetPlan>;
    onToggleRoi: (roiNumber: number) => void;
};

/** What the copy will do with this row, when that needs saying. */
function copyPlanNotice(
    intl: IntlShape,
    plan: CopyTargetPlan | undefined
): string | null {
    switch (plan?.kind) {
        case undefined:
        case 'create':
            return null;
        case 'overwrite':
            return intl.formatMessage({
                id: 'structureSet.copy.nameConflictOverwrite',
                defaultMessage:
                    'This structure will overwrite the existing one in the target.',
            });
        case 'skip':
            return copySkipNotice(intl, plan.reason);
    }
}

function copySkipNotice(intl: IntlShape, reason: CopySkipReason): string {
    switch (reason.kind) {
        case 'name-taken':
            return intl.formatMessage({
                id: 'structureSet.copy.nameConflict',
                defaultMessage:
                    'A structure with this name already exists in the target.',
            });
        case 'ambiguous':
            return intl.formatMessage({
                id: 'structureSet.copy.nameConflictAmbiguous',
                defaultMessage:
                    'Several structures with this name exist in the target, so it is not clear which one to overwrite. Rename one of them to copy this structure.',
            });
        case 'approved':
            return intl.formatMessage({
                id: 'structureSet.copy.nameConflictApproved',
                defaultMessage:
                    'The structure with this name in the target is approved and cannot be overwritten.',
            });
        case 'locked':
            return intl.formatMessage({
                id: 'structureSet.copy.nameConflictLocked',
                defaultMessage:
                    'The structure with this name in the target is locked and cannot be overwritten.',
            });
        case 'repeated-in-selection':
            return intl.formatMessage({
                id: 'structureSet.copy.nameRepeatedInSelection',
                defaultMessage:
                    'Another selected structure has this name. Only the first one is copied.',
            });
    }
}

export default function StructureListPanel({
    filteredRois,
    selectedRois,
    disabledRoiNumbers,
    copyPlans,
    onToggleRoi,
}: StructureListPanelProps) {
    const intl = useIntl();
    const theme = useTheme();

    return (
        <List dense disablePadding sx={{ flex: 1, overflow: 'auto' }}>
            {filteredRois.length === 0 ? (
                <Box px={2} py={3} textAlign="center">
                    <Typography fontSize={12} color="text.secondary">
                        <FormattedMessage
                            id="structureSet.copy.noMatchingRois"
                            defaultMessage="No structures match the current filters."
                        />
                    </Typography>
                </Box>
            ) : (
                filteredRois.map((roi) => {
                    const isDisabled = disabledRoiNumbers.has(roi.roiNumber);
                    const notice = copyPlanNotice(
                        intl,
                        copyPlans.get(roi.roiNumber)
                    );
                    const checked =
                        !isDisabled && selectedRois.has(roi.roiNumber);

                    return (
                        <Box
                            key={roi.roiNumber}
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            px={1}
                            py={0.25}
                            sx={{
                                cursor: isDisabled ? 'default' : 'pointer',
                                '&:hover': isDisabled
                                    ? undefined
                                    : { bgcolor: 'action.hover' },
                                opacity: isDisabled ? 0.55 : 1,
                            }}
                            onClick={() =>
                                !isDisabled && onToggleRoi(roi.roiNumber)
                            }
                            data-testid={`roi-preview-${roi.roiNumber}`}
                        >
                            <Checkbox
                                size="small"
                                checked={checked}
                                disabled={isDisabled}
                                tabIndex={-1}
                                disableRipple
                                sx={{ p: 0.25 }}
                            />
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                    bgcolor: dicomColorToRgbString(roi.color),
                                    opacity: isDisabled ? 0.5 : 1,
                                }}
                            />
                            <Typography fontSize={12} noWrap sx={{ flex: 1 }}>
                                {roi.name}
                            </Typography>
                            {notice && (
                                <Tooltip placement="right" title={notice}>
                                    {/* Focusable, so the reason is reachable
                                        from the keyboard on a disabled row.
                                        A press on it must not toggle the
                                        row. */}
                                    <Box
                                        display="flex"
                                        flexShrink={0}
                                        tabIndex={0}
                                        role="img"
                                        aria-label={notice}
                                        onPointerDown={(event) =>
                                            event.stopPropagation()
                                        }
                                        onClick={(event) =>
                                            event.stopPropagation()
                                        }
                                        sx={{
                                            outline: 'none',
                                            '&:focus-visible': {
                                                outline: '1px solid',
                                                outlineColor: 'warning.main',
                                                outlineOffset: 2,
                                                borderRadius: '50%',
                                            },
                                        }}
                                    >
                                        <AlertTriangle
                                            size={12}
                                            color={theme.palette.warning.main}
                                        />
                                    </Box>
                                </Tooltip>
                            )}
                            <Typography
                                fontSize={11}
                                color="text.secondary"
                                noWrap
                            >
                                {roi.interpretedType || '—'}
                            </Typography>
                        </Box>
                    );
                })
            )}
        </List>
    );
}
